/**
 * Query keys and in-flight de-duplication for analytics reads (ANL-E04-S06, CMP-11.15). Pure
 * TypeScript with no React import, so it runs in the DOM-less unit runner.
 *
 * A key is the endpoint, its params sorted by name, and the tile's allow-listed `keys`. Two tiles
 * with the same key share one request while it is in flight; nothing is kept once it settles (no
 * global cache), so the next read of that key is a new request.
 */

/**
 * The `keys` a request carries (AC6). An endpoint that declares no allow-list is always called
 * without `keys`. Otherwise the tile's own keys are sent, sorted and once each; none requested
 * means none sent. A key outside the allow-list is a programming error (the server would refuse
 * it with ANALYTICS_UNKNOWN_QUERY_KEY), so it throws rather than being dropped silently.
 */
export const resolveRequestKeys = (
  allowed: readonly string[] | undefined,
  requested: readonly string[] | undefined,
): string[] | undefined => {
  if (!allowed || !requested || requested.length === 0) return undefined;
  const unknown = requested.filter((key) => !allowed.includes(key));
  if (unknown.length > 0) {
    throw new RangeError(`Analytics keys not allow-listed for this endpoint: ${unknown.join(", ")}`);
  }
  return [...new Set(requested)].sort(compare);
};

// Plain code-unit order, so a key never depends on the browser's locale.
const compare = (a: string, b: string) => (a < b ? -1 : a > b ? 1 : 0);

const byNameThenValue = ([nameA, valueA]: [string, string], [nameB, valueB]: [string, string]) =>
  nameA === nameB ? compare(valueA, valueB) : compare(nameA, nameB);

/** `endpoint?sorted params`, plus `#keys=a,b` when the tile asks for keys. */
export const buildQueryKey = (
  endpointId: string,
  params: URLSearchParams,
  keys?: readonly string[],
): string => {
  const sorted = new URLSearchParams([...params.entries()].sort(byNameThenValue));
  const keySet = keys && keys.length > 0 ? `#keys=${[...new Set(keys)].sort(compare).join(",")}` : "";
  return `${endpointId}?${sorted.toString()}${keySet}`;
};

/** Starts the one request for a key; `emit` hands each snapshot to every tile reading that key. */
export type InFlightStart<S> = (signal: AbortSignal, emit: (snapshot: S) => void) => Promise<void>;

export type InFlightRegistry<S> = {
  /**
   * Reads `key`: joins its in-flight request, or starts one with `start`. Returns `leave`; when
   * the last tile reading an unsettled request leaves, that request is aborted.
   */
  join(key: string, start: InFlightStart<S>, listener: (snapshot: S) => void): () => void;
  /**
   * Aborts every request, in flight or queued, except the one reading with `except`: none of them
   * emits again, and the next `join` of their keys starts a new request.
   */
  abortAll(except?: AbortSignal): void;
  /** Requests in flight, one per key. */
  readonly size: number;
};

type Entry<S> = {
  controller: AbortController;
  listeners: Set<(snapshot: S) => void>;
  latest?: { snapshot: S };
};

export const createInFlightRegistry = <S>(): InFlightRegistry<S> => {
  const entries = new Map<string, Entry<S>>();

  const removeIfCurrent = (key: string, entry: Entry<S>) => {
    if (entries.get(key) === entry) entries.delete(key);
  };

  const begin = (key: string, start: InFlightStart<S>, listener: (snapshot: S) => void): Entry<S> => {
    const entry: Entry<S> = { controller: new AbortController(), listeners: new Set([listener]) };
    entries.set(key, entry);
    const emit = (snapshot: S) => {
      if (entry.controller.signal.aborted) return;
      entry.latest = { snapshot };
      for (const each of [...entry.listeners]) each(snapshot);
    };
    // `start` resolves once the request settled (or was aborted); it reports failures as
    // snapshots, so a rejection here is a defect and is left to surface as unhandled.
    void start(entry.controller.signal, emit).finally(() => removeIfCurrent(key, entry));
    return entry;
  };

  const joinExisting = (entry: Entry<S>, listener: (snapshot: S) => void): Entry<S> => {
    entry.listeners.add(listener);
    if (entry.latest) listener(entry.latest.snapshot);
    return entry;
  };

  return {
    join(key, start, listener) {
      const existing = entries.get(key);
      const entry = existing ? joinExisting(existing, listener) : begin(key, start, listener);
      return () => {
        entry.listeners.delete(listener);
        if (entry.listeners.size === 0 && entries.get(key) === entry) {
          entries.delete(key);
          entry.controller.abort();
        }
      };
    },
    abortAll(except) {
      for (const [key, entry] of [...entries]) {
        if (entry.controller.signal === except) continue;
        entries.delete(key);
        entry.controller.abort();
      }
    },
    get size() {
      return entries.size;
    },
  };
};
